import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabase';
import { userProfileCache } from '../stores/referenceDataCache';

interface EmailDomainStatus {
  hasLegacy: boolean;
  hasMailjetVerified: boolean;
  needsMigration: boolean;
  loading: boolean;
  refetch: () => void;
}

let cache: { data: Omit<EmailDomainStatus, 'loading' | 'refetch'>; expiresAt: number } | null = null;
const TTL_MS = 30_000;

/**
 * Wave 3.6 — Status email_domains per org corrente.
 * - hasLegacy: org ha ≥1 dominio SendGrid verified (caso 4 org legacy backfillate)
 * - hasMailjetVerified: org ha dominio Mailjet verified+is_default (post-migrazione)
 * - needsMigration: hasLegacy && !hasMailjetVerified → mostra banner+empty state
 * Cache 30s in-memory. refetch() per forzare re-query (es. post verify).
 */
export function useEmailDomainStatus(): EmailDomainStatus {
  const [status, setStatus] = useState<Omit<EmailDomainStatus, 'loading' | 'refetch'>>({
    hasLegacy: false,
    hasMailjetVerified: false,
    needsMigration: false,
  });
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (cache && cache.expiresAt > Date.now()) {
      setStatus(cache.data);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }
    const organizationId = await userProfileCache.getOrganizationId(user.id).catch(() => null);
    if (!organizationId) {
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('email_domains')
      .select('provider, status, is_default')
      .eq('organization_id', organizationId);

    if (error || !data) {
      setLoading(false);
      return;
    }
    const hasLegacy = data.some((d) => d.provider === 'sendgrid' && d.status === 'verified');
    const hasMailjetVerified = data.some(
      (d) => d.provider === 'mailjet' && d.status === 'verified' && d.is_default === true,
    );
    const result = {
      hasLegacy,
      hasMailjetVerified,
      needsMigration: hasLegacy && !hasMailjetVerified,
    };
    cache = { data: result, expiresAt: Date.now() + TTL_MS };
    setStatus(result);
    setLoading(false);
  }, []);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  // Listen for cross-component invalidation triggered after sender save / domain mutations.
  useEffect(() => {
    const handler = () => {
      cache = null;
      void fetchStatus();
    };
    window.addEventListener('email-domain-status-changed', handler);
    return () => window.removeEventListener('email-domain-status-changed', handler);
  }, [fetchStatus]);

  const refetch = useCallback(() => {
    cache = null;
    void fetchStatus();
  }, [fetchStatus]);

  return { ...status, loading, refetch };
}

/** Notify all useEmailDomainStatus subscribers to invalidate cache and refetch. */
export function notifyEmailDomainStatusChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('email-domain-status-changed'));
  }
}
